/* eslint-disable  react/no-multi-comp */
/*  eslint-disable react/display-name */
/*  eslint-disable react/prop-types */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { Form, useFormikContext } from 'formik';
import { FilterValues } from '../types';

import connect from '@app/utils/connect';
import Provider from './FiltersFormContext';

import RspFiltersRow from './RspFiltersRow';
import InsightsFiltersRow from './InsightsFiltersRow';
import InsightsGroupByRow from './InsightsGroupByRow';
import SmpFiltersRow from './SmpFiltersRow';
import BroadcastsFiltersRow from './BroadcastsFiltersRow';
import DemographicsFiltersRow from './DemographicsFiltersRow';
import {
    Button as AntButton,
    Flex,
    Collapse,
    Typography,
    ConfigProvider,
    Tooltip,
} from '@app/ui';

import '../Filters.scss';

import { FILTER_ORIGIN } from '@app/constants/filters';
import { BroadcastTab } from '@app/broadcasts/utils/broadcastsUtils';
import SortByRow from './SortByRow';

const collapseTheme = {
    components: {
        Collapse: {
            headerBg: '#FAFAFA',
            colorBorder: 'transparent',
            contentPadding: 0,
        },
    },
};

const collapseLabelStyle = {
    margin: 0,
    color: '#1f1f1f',
    fontWeight: 400,
};

const FiltersForm = ({
    origin,
    isLoading,
    isOverridingFilters = false,
    clearOverrideFilters,
}) => {
    const { t } = useTranslation();
    const { activeTab: currentTab } = useParams();
    const { submitForm, resetForm, values } = useFormikContext<FilterValues>();
    // Age Bracket / Gender only apply to the Broadcast Demographics tab.
    const showDemographicFilters =
        origin === FILTER_ORIGIN.BROADCAST_EXPOSURES &&
        currentTab === BroadcastTab.DEMOGRAPHICS;
    const hasPageFilters =
        origin !== FILTER_ORIGIN.HOME &&
        origin !== FILTER_ORIGIN.SETTINGS &&
        origin !== FILTER_ORIGIN.AUTO_INSIGHTS;
    const showDefaultFilters = origin === FILTER_ORIGIN.SETTINGS;

    const pageSpecificFilters = () => {
        switch (origin) {
            case FILTER_ORIGIN.INSIGHTS:
                return <InsightsFiltersRow />;
            case FILTER_ORIGIN.SMP:
                return <SmpFiltersRow />;
            case FILTER_ORIGIN.BROADCAST_EXPOSURES:
                return <BroadcastsFiltersRow />;
            default:
                return;
        }
    };

    const preventEnterCallback = e => {
        if (e.key === 'Enter') {
            e.preventDefault();
        }
    };

    const selectedGroupBys: string[] = Object.entries(
        values.insightsGroupBy || {},
    ).reduce((acc: string[], [key, value]) => {
        if (value) {
            acc.push(key);
        }
        return acc;
    }, []);

    return (
        <>
            <Form onKeyDown={preventEnterCallback}>
                <Provider
                    isLoading={isLoading}
                    origin={origin}
                    isOverridingFilters={isOverridingFilters}
                >
                    <Flex vertical gap="small">
                        <RspFiltersRow />

                        {hasPageFilters && (
                            <ConfigProvider theme={collapseTheme}>
                                <Collapse
                                    defaultActiveKey={['1']}
                                    items={[
                                        {
                                            key: '1',
                                            label: (
                                                <Typography.Text
                                                    style={collapseLabelStyle}
                                                >
                                                    {t(
                                                        'filters.pageSpecificFilters',
                                                    )}
                                                </Typography.Text>
                                            ),
                                            children: pageSpecificFilters(),
                                        },
                                    ]}
                                    style={{
                                        marginTop: '16px',
                                    }}
                                />
                            </ConfigProvider>
                        )}

                        {showDemographicFilters && (
                            <ConfigProvider theme={collapseTheme}>
                                <Collapse
                                    defaultActiveKey={['1']}
                                    data-test="demographic-filters-section"
                                    items={[
                                        {
                                            key: '1',
                                            label: (
                                                <Typography.Text
                                                    style={collapseLabelStyle}
                                                >
                                                    {t(
                                                        'filters.demographicFilters',
                                                    )}
                                                </Typography.Text>
                                            ),
                                            children: (
                                                <DemographicsFiltersRow />
                                            ),
                                        },
                                    ]}
                                />
                            </ConfigProvider>
                        )}

                        <div className="-mvc-divider" />

                        <Flex
                            gap="small"
                            wrap
                            justify="flex-end"
                            align="flex-end"
                        >
                            {origin === FILTER_ORIGIN.INSIGHTS && (
                                <InsightsGroupByRow />
                            )}
                            {origin === FILTER_ORIGIN.SMP && <SortByRow />}
                            {isOverridingFilters &&
                                (origin === FILTER_ORIGIN.BROADCAST_EXPOSURES ||
                                    origin === FILTER_ORIGIN.SMP) && (
                                    <AntButton
                                        type="primary"
                                        color="primary"
                                        variant="solid"
                                        data-test="reset-filters-button"
                                        onClick={async () => {
                                            clearOverrideFilters();
                                            await submitForm();
                                        }}
                                        icon={
                                            <i className="fa-regular fa-arrow-rotate-right -m-r-2" />
                                        }
                                    >
                                        {t('filters.resetFilters')}
                                    </AntButton>
                                )}
                            <AntButton
                                type="primary"
                                color="default"
                                variant="solid"
                                data-test="new-update-filter"
                                disabled={
                                    isOverridingFilters &&
                                    (origin ===
                                        FILTER_ORIGIN.BROADCAST_EXPOSURES ||
                                        origin === FILTER_ORIGIN.SMP)
                                }
                                onClick={() =>
                                    resetForm({
                                        values: {
                                            ...values,
                                            rightsholders: [],
                                            placements: [],
                                            startDate: null,
                                            endDate: null,
                                            rightsholdersLeaguePairs: [],
                                            sponsors: [],
                                            placementTypes: [],
                                            insightsIds: [],
                                            platforms: {},
                                            compareDates: null,
                                            accTypes: {},
                                            mediaTypes: [],
                                            collections: [],
                                            hashtags: [],
                                            caption: '',
                                            handles: [],
                                            authorshipTypes: [],
                                            collaboratingAccountUsernames: [],
                                            broadcastNetworks: [],
                                            broadcastNames: [],
                                            broadcastOptions: [],
                                            dateRange: null,
                                            ageBrackets: [],
                                            genders: [],
                                        },
                                    })
                                }
                                icon={<i className="fa-regular fa-x -m-r-2" />}
                            >
                                {t('filters.clearAllFilters')}
                            </AntButton>
                            <Tooltip
                                title={
                                    origin === FILTER_ORIGIN.INSIGHTS &&
                                    !selectedGroupBys.length
                                        ? t('filters.selectGroupByFirst')
                                        : undefined
                                }
                                placement="topRight"
                            >
                                <AntButton
                                    type="primary"
                                    color="primary"
                                    variant="solid"
                                    data-test="new-update-filter"
                                    icon={
                                        <i className="fa-regular fa-arrow-rotate-right -m-r-2" />
                                    }
                                    onClick={submitForm}
                                    disabled={
                                        isLoading ||
                                        (origin === FILTER_ORIGIN.INSIGHTS &&
                                            !selectedGroupBys.length) ||
                                        (isOverridingFilters &&
                                            (origin ===
                                                FILTER_ORIGIN.BROADCAST_EXPOSURES ||
                                                origin === FILTER_ORIGIN.SMP))
                                    }
                                >
                                    {showDefaultFilters
                                        ? t('filters.updateDefaultFilters')
                                        : t('filters.updateFilters')}
                                </AntButton>
                            </Tooltip>
                        </Flex>
                    </Flex>
                </Provider>
            </Form>
        </>
    );
};

export default connect(['client'])(FiltersForm);
